<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex items-center justify-center bg-black/70 p-4 sm:p-6 z-[10000]"
    @click.self="emit('close')"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 ease-in-out"
      :class="{
        'scale-100 opacity-100': isOpen,
        'scale-95 opacity-0': !isOpen,
      }"
    >
      <div
        class="flex items-center justify-between p-6 border-b border-gray-100"
      >
        <h2 class="flex items-center gap-3 text-2xl font-bold text-gray-900">
          <GraduationCap class="w-6 h-6 text-indigo-600" />
          Teacher Details
        </h2>
        <button
          @click="emit('close')"
          class="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors duration-150"
          title="Close"
        >
          <X class="w-6 h-6" />
        </button>
      </div>
      <div
        v-if="teacher"
        class="p-6 sm:p-8 space-y-6 sm:space-y-8"
      >
        <!-- Header -->
        <div
          class="flex flex-col sm:flex-row items-center sm:items-start space-y-6 sm:space-y-0 sm:space-x-8 pb-8 border-b border-slate-200/80"
        >
          <div class="flex-shrink-0">
            <div class="w-36 h-36 sm:w-40 sm:h-40">
              <!-- If Photo Exists -->
              <img
                v-if="teacher.photo"
                :src="teacher.photo"
                :alt="teacher.name"
                class="w-full h-full rounded-2xl object-cover shadow-xl ring-1 ring-slate-200"
              />

              <!-- If No Photo → Show Initials -->
              <div
                v-else
                class="w-full h-full rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-4xl font-bold shadow-xl ring-1 ring-slate-200"
              >
                {{ getInitials(teacher.name) }}
              </div>
            </div>
          </div>
          <div class="text-center sm:text-left flex-1 w-full">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
              <!-- Teacher Name -->
              <h3
                class="flex items-center gap-3 text-xl sm:text-3xl font-bold text-slate-900 break-words"
              >
                <User
                  class="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600 flex-shrink-0"
                />
                <span class="truncate sm:whitespace-normal">
                  {{ teacher.name }}
                </span>
              </h3>

              <!-- Leave Balance Button -->
              <button
                @click="showLeaveBalance = true"
                class="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-semibold shadow-md"
              >
                <Calendar class="w-4 h-4" />
                View Leave Balance
              </button>
            </div>
            <div class="mt-3 space-y-2">
              <div class="flex items-center gap-3 text-slate-600">
                <span
                  class="flex items-center gap-2 text-sm font-medium text-slate-500 uppercase tracking-wide"
                >
                  <Shield class="w-4 h-4 text-slate-400" />
                  ID
                </span>
                <span
                  class="font-mono text-slate-700 bg-slate-100/80 px-3 py-1 rounded-lg inline-block"
                >
                  {{ teacher.attendanceID }}
                </span>
              </div>
              <div class="flex items-center gap-3 text-slate-600">
                <span
                  class="flex items-center gap-2 text-sm font-medium text-slate-500 uppercase tracking-wide"
                >
                  <Shield class="w-4 h-4 text-slate-400" />
                  Role
                </span>
                <span
                  class="font-mono text-slate-700 bg-slate-100/80 px-3 py-1 rounded-lg inline-block capitalize"
                >
                  {{ teacher.role }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Contact Info -->
          <div
            class="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200/50 hover:border-blue-300/80 transition-all duration-200 hover:shadow-lg"
          >
            <h4
              class="text-lg font-bold text-slate-900 mb-5 flex items-center border-b border-blue-200/60 pb-3"
            >
              <User class="w-5 h-5 mr-3 text-blue-600" /> Contact Information
            </h4>
            <div class="space-y-4">
              <!-- Email -->
              <div class="flex items-start gap-3">
                <Mail class="w-4 h-4 text-blue-500 mt-1" />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                  >
                    Email
                  </p>
                  <p class="text-slate-700 text-sm break-all">
                    {{ teacher.email }}
                  </p>
                </div>
              </div>

              <!-- Phone -->
              <div class="flex items-start gap-3">
                <Phone class="w-4 h-4 text-blue-500 mt-1" />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                  >
                    Phone
                  </p>
                  <p class="text-slate-700 text-sm font-medium">
                    {{ teacher.phone }}
                  </p>
                </div>
              </div>

              <!-- Date of Birth -->
              <div class="flex items-start gap-3">
                <Cake class="w-4 h-4 text-blue-500 mt-1" />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                  >
                    Date of Birth
                  </p>
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="text-slate-700 text-sm">
                      {{ formatDate(teacher.dob) }}
                    </p>
                    <span
                      v-if="teacher?.dob"
                      class="text-slate-600 text-xs bg-blue-100/80 px-2 py-1 rounded-lg"
                    >
                      ({{ calculateAge(teacher.dob) }} yrs)
                    </span>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div class="flex items-start gap-3">
                <Circle
                  class="w-4 h-4 mt-1"
                  :class="
                    teacher.status === 'active'
                      ? 'text-emerald-500'
                      : 'text-rose-500'
                  "
                />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
                  >
                    Status
                  </p>
                  <span
                    :class="
                      teacher.status === 'active'
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                        : 'bg-rose-100 text-rose-700 border-rose-200'
                    "
                    class="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold border capitalize"
                  >
                    {{ teacher.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- Subject & Aadhaar -->
          <div
            class="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-2xl border border-purple-200/50 hover:border-purple-300/80 transition-all duration-200 hover:shadow-lg"
          >
            <h4
              class="text-lg font-bold text-slate-900 mb-5 flex items-center border-b border-purple-200/60 pb-3"
            >
              <Briefcase class="w-5 h-5 mr-3 text-purple-600" /> Professional &
              ID
            </h4>
            <div class="space-y-5">
              <!-- Subject -->
              <div class="flex items-start gap-3">
                <BookOpen class="w-4 h-4 text-purple-500 mt-1" />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
                  >
                    Subject Teaching
                  </p>
                  <span
                    class="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-purple-100/80 text-purple-700 border border-purple-200"
                  >
                    {{ teacher.subject }}
                  </span>
                </div>
              </div>

              <!-- Aadhaar Number -->
              <div
                v-if="teacher.aadhaarNumber"
                class="flex items-start gap-3"
              >
                <CreditCard class="w-4 h-4 text-purple-500 mt-1" />
                <div>
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1"
                  >
                    Aadhaar Number
                  </p>
                  <p
                    class="text-slate-700 text-sm font-mono bg-slate-100/50 px-3 py-2 rounded-lg inline-block"
                  >
                    {{ teacher.aadhaarNumber }}
                  </p>
                </div>
              </div>

              <!-- Aadhaar Card Document -->
              <div
                v-if="teacher.aadhaarCard"
                class="pt-4 border-t border-purple-200/40"
              >
                <div class="flex items-center gap-2 mb-3">
                  <FileText class="w-4 h-4 text-purple-500" />
                  <p
                    class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                  >
                    Aadhaar Card Document
                  </p>
                </div>

                <div v-if="isImage(teacher.aadhaarCard)">
                  <img
                    :src="teacher.aadhaarCard"
                    alt="Aadhaar Card"
                    class="max-w-full h-auto rounded-xl border-2 border-purple-200 shadow-md hover:shadow-lg transition-shadow"
                  />
                </div>

                <div v-else-if="isPdf(teacher.aadhaarCard)">
                  <a
                    :href="teacher.aadhaarCard"
                    target="_blank"
                    rel="noopener noreferrer"
                    :download="
                      teacher.aadhaarCard.startsWith('data:')
                        ? 'aadhaar_card.pdf'
                        : null
                    "
                    class="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium bg-purple-50 px-4 py-2 rounded-lg border border-purple-200 hover:border-purple-300 transition-all hover:shadow-sm"
                  >
                    <Paperclip class="w-4 h-4" />
                    View / Download Document
                  </a>
                </div>

                <div v-else>
                  <a
                    :href="teacher.aadhaarCard"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium bg-purple-50 px-4 py-2 rounded-lg border border-purple-200 hover:border-purple-300 transition-all hover:shadow-sm"
                  >
                    <Paperclip class="w-4 h-4" />
                    View Document (Unsupported Type)
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Address -->
        <div
          class="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-2xl border border-amber-200/50 hover:border-amber-300/80 transition-all duration-200 hover:shadow-lg"
        >
          <h4
            class="text-lg font-bold text-slate-900 mb-5 flex items-center border-b border-amber-200/60 pb-3"
          >
            <MapPin class="w-5 h-5 mr-3 text-amber-600" /> Address Details
          </h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <!-- VTC -->
            <div
              v-if="teacher.vtc"
              class="flex items-start gap-3"
            >
              <Home class="w-4 h-4 text-amber-500 mt-1" />
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  VTC
                </p>
                <p class="text-slate-700 text-sm">
                  {{ teacher.vtc }}
                </p>
              </div>
            </div>

            <!-- Post Office -->
            <div
              v-if="teacher.postOffice"
              class="flex items-start gap-3"
            >
              <Mailbox class="w-4 h-4 text-amber-500 mt-1" />
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  Post Office
                </p>
                <p class="text-slate-700 text-sm">
                  {{ teacher.postOffice }}
                </p>
              </div>
            </div>

            <!-- Sub-District -->
            <div
              v-if="teacher.subDistrict"
              class="flex items-start gap-3"
            >
              <Map class="w-4 h-4 text-amber-500 mt-1" />
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  Sub-District
                </p>
                <p class="text-slate-700 text-sm">
                  {{ teacher.subDistrict }}
                </p>
              </div>
            </div>

            <!-- State -->
            <div
              v-if="teacher.state"
              class="flex items-start gap-3"
            >
              <Building class="w-4 h-4 text-amber-500 mt-1" />
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  State
                </p>
                <p class="text-slate-700 text-sm">
                  {{ teacher.state }}
                </p>
              </div>
            </div>

            <!-- Pincode -->
            <div
              v-if="teacher.pincode"
              class="flex items-start gap-3"
            >
              <Hash class="w-4 h-4 text-amber-500 mt-1" />
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                >
                  Pincode
                </p>
                <p
                  class="text-slate-700 text-sm font-mono bg-slate-100/50 px-2 py-1 rounded inline-block"
                >
                  {{ teacher.pincode }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <!-- Qualifications -->
        <div
          v-if="teacher.qualifications && teacher.qualifications.length > 0"
          class="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-2xl border border-teal-200/50 hover:border-teal-300/80 transition-all duration-200 hover:shadow-lg"
        >
          <h4
            class="text-lg font-bold text-slate-900 mb-5 flex items-center border-b border-teal-200/60 pb-3"
          >
            <GraduationCap class="w-5 h-5 mr-3 text-teal-600" /> Qualifications
          </h4>
          <ul class="list-none pl-0 space-y-4">
            <li
              v-for="(q, index) in teacher.qualifications"
              :key="index"
              class="bg-white/60 border border-teal-100/50 rounded-xl p-4 hover:bg-white/80 transition-all"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3"
              >
                <div>
                  <p
                    class="text-sm font-bold text-slate-500 uppercase tracking-wide mb-1"
                  >
                    Qualification Type
                  </p>
                  <p class="text-slate-900 font-bold">{{ q.type }}</p>
                </div>
                <div class="text-right">
                  <p
                    class="text-sm font-bold text-slate-500 uppercase tracking-wide mb-1"
                  >
                    Year
                  </p>
                  <p class="text-slate-900 font-bold text-lg">{{ q.year }}</p>
                </div>
              </div>
              <div class="pb-3 border-t border-teal-100/50 pt-3">
                <p class="text-sm text-slate-600">
                  from
                  <span class="font-semibold text-slate-800">{{
                    q.institution
                  }}</span>
                </p>
              </div>
              <div v-if="q.fileUrl">
                <a
                  v-if="isPdf(q.fileUrl)"
                  :href="q.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  :download="
                    q.fileUrl.startsWith('data:')
                      ? `qualification_${index + 1}.pdf`
                      : null
                  "
                  class="text-teal-600 hover:text-teal-700 hover:underline flex items-center text-sm font-medium bg-teal-50 px-3 py-2 rounded-lg border border-teal-200 hover:border-teal-300 inline-flex transition-all"
                >
                  <Paperclip class="w-4 h-4 mr-2" /> View/Download Document
                </a>
                <img
                  v-else-if="isImage(q.fileUrl)"
                  :src="q.fileUrl"
                  alt="Qualification Document"
                  class="max-w-full h-auto rounded-xl border-2 border-teal-200 shadow-md hover:shadow-lg transition-shadow mt-3"
                />
                <a
                  v-else
                  :href="q.fileUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-teal-600 hover:text-teal-700 hover:underline flex items-center text-sm font-medium bg-teal-50 px-3 py-2 rounded-lg border border-teal-200 hover:border-teal-300 inline-flex transition-all"
                >
                  <Paperclip class="w-4 h-4 mr-2" /> View Document (Unsupported
                  Type)
                </a>
              </div>
            </li>
          </ul>
        </div>

        <!-- Remarks -->
        <div
          v-if="teacher.remark"
          class="bg-gradient-to-br from-rose-50 to-pink-50 p-6 rounded-2xl border border-rose-200/50 hover:border-rose-300/80 transition-all duration-200 hover:shadow-lg"
        >
          <h4
            class="text-lg font-bold text-slate-900 mb-4 flex items-center border-b border-rose-200/60 pb-3"
          >
            <MessageSquare class="w-5 h-5 mr-3 text-rose-600" /> Remarks
          </h4>
          <p class="text-slate-700 leading-relaxed">{{ teacher.remark }}</p>
        </div>
      </div>
      <!-- Attendance Analytics -->
      <div v-if="teacher?._id">
        <TeacherAttendanceAnalytics
          :key="teacher?._id"
          :userId="teacher._id"
          :userName="teacher.name"
          :selectedYear="selectedYear"
          :attendanceData="attendanceData"
          @update:selectedYear="handleYearChange"
          @filter-change="handleFilterChange"
        />
      </div>
      <div
        v-else
        class="p-8 sm:p-12 text-center"
      >
        <div class="mb-4">
          <User class="w-12 h-12 text-slate-300 mx-auto" />
        </div>
        <p class="text-slate-400 text-lg font-medium">
          No teacher data available for display.
        </p>
      </div>
    </div>
  </div>
  <LeaveBalanceModal
    v-if="showLeaveBalance && teacher"
    v-model="showLeaveBalance"
    :userId="teacher._id"
    :userName="teacher.name"
  />
</template>

<script setup>
  import { defineProps, defineEmits, ref, computed, watch } from "vue";
  import { useStore } from "vuex";
  import LeaveBalanceModal from "@/components/LeaveBalance.vue";
  import TeacherAttendanceAnalytics from "@/components/Teacher/AttendanceGraph.vue";
  import {
    X,
    Paperclip,
    User,
    Briefcase,
    MapPin,
    GraduationCap,
    MessageSquare,
    Calendar,
    Shield,
    Mail,
    Phone,
    Cake,
    Circle,
    BookOpen,
    CreditCard,
    FileText,
    Home,
    Mailbox,
    Map,
    Building,
    Hash,
  } from "lucide-vue-next";

  // eslint-disable-next-line no-unused-vars
  const props = defineProps({
    teacher: {
      type: Object,
      default: null,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
  });
  const store = useStore();
  const startDate = ref(null);
  const endDate = ref(null);
  const selectedYear = ref(new Date().getFullYear());

  const attendanceData = computed(() => {
    const userData = store.state.teacherAttendanceYearly?.[props.teacher?._id];

    if (!userData) return {};

    const rangeKey =
      startDate.value && endDate.value
        ? `${selectedYear.value}_${startDate.value}_${endDate.value}`
        : `${selectedYear.value}_full`;

    return userData?.[rangeKey] ?? {};
  });
  const loadAttendance = async () => {
    if (!props.teacher?._id) return;

    await store.dispatch("fetchTeacherYearlyAttendance", {
      userId: props.teacher._id,
      year: selectedYear.value,
      startDate: startDate.value,
      endDate: endDate.value,
    });
  };
  const handleYearChange = (year) => {
    selectedYear.value = year;

    // reset range when year changes
    startDate.value = null;
    endDate.value = null;
  };

  const handleFilterChange = async ({ year, startDate: s, endDate: e }) => {
    startDate.value = s;
    endDate.value = e;

    await store.dispatch("fetchTeacherYearlyAttendance", {
      userId: props.teacher._id,
      year,
      startDate: s,
      endDate: e,
    });
  };
  watch(
    () => [props.teacher?._id, selectedYear.value],
    () => {
      loadAttendance();
    },
    { immediate: true },
  );
  const emit = defineEmits(["close"]);
  const showLeaveBalance = ref(false);
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateString;
    }
  };
  const calculateAge = (dob) => {
    if (!dob) return "N/A";

    const birth = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  };

  const isImage = (urlOrBase64) => {
    if (!urlOrBase64) return false;
    // Check for common image file extensions in URLs
    if (/\.(jpeg|jpg|gif|png|svg|webp)$/i.test(urlOrBase64)) {
      return true;
    }
    // Check for Base64 image data URLs
    if (urlOrBase64.startsWith("data:image/")) {
      return true;
    }
    return false;
  };

  const isPdf = (urlOrBase64) => {
    if (!urlOrBase64) return false;
    // Check for .pdf extension in URLs
    if (/\.pdf$/i.test(urlOrBase64)) {
      return true;
    }
    // Check for Base64 PDF data URLs
    if (urlOrBase64.startsWith("data:application/pdf")) {
      return true;
    }
    return false;
  };
  const getInitials = (name) => {
    if (!name) return "";

    return name
      .trim()
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
</script>

<style scoped>
  /* Basic modal transition styles */
  .scale-95 {
    transform: scale(0.95);
  }
  .opacity-0 {
    opacity: 0;
  }
  .scale-100 {
    transform: scale(1);
  }
  .opacity-100 {
    opacity: 1;
  }
</style>
